import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import SPAPill from '@/components/admin/SysadminSPA/shared/SPAPill';
import rolePillTone from '@/components/admin/SysadminSPA/SupportView/UserSearch/rolePillTone';
import type { AuditActor, AuditRow } from './auditLogRows';

const columnHelper = createColumnHelper<AuditRow>();

const dateFormat = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
const timeFormat = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });

const WhenCell = ({ iso }: { iso: string }) => {
  const date = new Date(iso);

  return (
    <div className="tw-whitespace-nowrap">
      <div>{dateFormat.format(date)}</div>
      <div className="tw-text-xs tw-text-spa-tertiary">{timeFormat.format(date)}</div>
    </div>
  );
};

const WhoCell = ({ actor, impersonated }: { actor: AuditActor; impersonated: AuditActor | null }) => (
  <div>
    <div className="tw-flex tw-items-center tw-gap-2">
      <span className="tw-font-bold tw-text-spa-heading">{actor.name}</span>
      <SPAPill tone={rolePillTone(actor.role.toLowerCase())}>{actor.role}</SPAPill>
    </div>
    {impersonated && (
      <div className="tw-mt-1 tw-text-xs tw-text-spa-warning">
        Impersonating {impersonated.name}
      </div>
    )}
  </div>
);

const ChangeCell = ({ from, to }: { from: string; to: string }) => {
  if (!from && !to) return null;

  return (
    <span className="tw-whitespace-nowrap">
      <span className="tw-text-spa-tertiary">{from}</span>
      <span aria-hidden="true" className="tw-mx-1.5 tw-text-spa-tertiary">→</span>
      <span className="tw-font-bold tw-text-spa-heading">{to}</span>
    </span>
  );
};

const getAuditLogColumns = (): ColumnDef<AuditRow, unknown>[] => [
  columnHelper.accessor('changedAt', {
    cell: info => <WhenCell iso={info.getValue()} />,
    header: 'When',
    size: 140,
  }),
  columnHelper.accessor(row => `${row.actor.name} ${row.impersonated?.name ?? ''}`, {
    cell: info => <WhoCell actor={info.row.original.actor} impersonated={info.row.original.impersonated} />,
    header: 'Who',
    id: 'who',
    size: 240,
  }),
  columnHelper.accessor(row => (row.subject ? `${row.label} ${row.subject}` : row.label), {
    cell: info => (
      <div>
        <div>{info.row.original.label}</div>
        {info.row.original.subject && (
          <div className="tw-text-xs tw-text-spa-tertiary">{info.row.original.subject}</div>
        )}
      </div>
    ),
    header: 'Setting',
    id: 'setting',
  }),
  columnHelper.accessor(row => `${row.from} → ${row.to}`, {
    cell: info => <ChangeCell from={info.row.original.from} to={info.row.original.to} />,
    enableSorting: false,
    header: 'Change',
    id: 'change',
    size: 200,
  }),
];

export default getAuditLogColumns;
